import { ExclamationCircleOutlined, RightOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Col, Popover, Row, Space } from 'antd';
import { eq } from 'lodash';
import { ClientServicesLink, CurrencyField, MTypography } from 'src/lib';
import { AccountBalanceSummaryView } from 'src/models';
import { ColorConstant, Display, FlexDirection, Images, Spacing } from 'src/styles';

import * as Styles from './SummaryInfo.styles';

const titleRowStyle = css`
  width: 100%;
`;

const balanceSummaryFieldRowStyle = css`
  width: 100%;
  border-radius: 2px;
  ${Spacing.p12}
`;

const summaryInfoRowStyle = css`
  width: 100%;
  ${Display.flex}
  ${FlexDirection.column}
  ${Spacing.ml16}
`;

interface BalanceSummaryFieldProps {
  label: string;
  value?: number;
  color?: string;
  onClick?: () => void;
  hasError?: boolean;
  error?: string;
  alwaysShow?: boolean;
}

const BalanceSummaryField = ({
  label,
  value,
  color,
  hasError,
  error,
  alwaysShow = false,
  onClick,
}: BalanceSummaryFieldProps) => {
  if (eq(value, 0) && !hasError && !alwaysShow) {
    return null;
  }

  return (
    <Row align='middle' justify='space-between' className={balanceSummaryFieldRowStyle}>
      <Col>
        <MTypography variant='h6' weight='semibold' color={color}>
          {label}
        </MTypography>
      </Col>
      <Col>
        {hasError ? (
          <Popover
            placement='topRight'
            content={
              <MTypography variant='body1' color={ColorConstant.RED7}>
                {error ?? (
                  <>
                    An unexpected error occurred. Please try again or contact <ClientServicesLink /> for assistance.
                  </>
                )}
              </MTypography>
            }
            overlayClassName={Styles.popoverWrapper}
            overlayInnerStyle={{ borderRadius: '2px', padding: '12px 16px 13px 16px' }}>
            <ExclamationCircleOutlined style={{ fontSize: '24px', color: ColorConstant.RED7 }} />
          </Popover>
        ) : (
          <MTypography variant='h6' weight='semibold' color={color}>
            <Row align='middle'>
              <Space size={16} direction='horizontal'>
                <CurrencyField value={value} />
                {onClick && <RightOutlined onClick={onClick} style={{ color: ColorConstant.GRAY7 }} />}
              </Space>
            </Row>
          </MTypography>
        )}
      </Col>
    </Row>
  );
};

interface BalanceSummaryProps {
  summary: AccountBalanceSummaryView;
  onRefresh: () => void;
}

export const BalanceSummary = ({ summary, onRefresh }: BalanceSummaryProps) => {
  return (
    <Row className={summaryInfoRowStyle}>
      <Row justify='space-between' className={cx(titleRowStyle, Spacing.mx4, Spacing.mb16)}>
        <Col span={20} data-testid={'summary-account-details'}>
          <MTypography variant='h5' weight='bold' color={ColorConstant.GRAY7}>
            Account Details
          </MTypography>
        </Col>
        <Col className={cx(Styles.refresh, Styles.alignRight)} style={{ color: '#0255A3' }} onClick={onRefresh}>
          <Space size={4}>
            <img src={Images.ReloadIcon} alt='refresh' />
            <span>Refresh</span>
          </Space>
        </Col>
      </Row>
      <Row>
        <Space direction='vertical' size={16} className={titleRowStyle}>
          <BalanceSummaryField
            label='Cash Available'
            value={summary.cashAvailable?.value}
            hasError={summary.cashAvailable?.hasError}
            error={summary.cashAvailable?.error}
            color={summary.cashAvailable?.color}
            alwaysShow
          />
          <BalanceSummaryField
            label='Pending Money Transactions'
            value={summary.pendingTransations?.value}
            hasError={summary.pendingTransations?.hasError}
            error={summary.pendingTransations?.error}
            onClick={summary.pendingTransations?.onClick}
            color={summary.pendingTransations?.color}
          />
          <BalanceSummaryField
            label='Securities Holdings'
            value={summary.securities?.value}
            hasError={summary.securities?.hasError}
            error={summary.securities?.error}
            color={summary.securities?.color}
            alwaysShow
          />
          <BalanceSummaryField
            label='Non-Traded Holdings'
            value={summary.nonTradedHoldings?.value}
            hasError={summary.nonTradedHoldings?.hasError}
            error={summary.nonTradedHoldings?.error}
            color={summary.nonTradedHoldings?.color}
            alwaysShow
          />
          <BalanceSummaryField
            label='Working Securities Orders'
            value={summary.pendingOrders?.value}
            hasError={summary.pendingOrders?.hasError}
            error={summary.pendingOrders?.error}
            onClick={summary.pendingOrders?.onClick}
            color={summary.pendingOrders?.color}
            alwaysShow
          />
          <BalanceSummaryField
            label='Pending Offer Orders'
            value={summary.pendingOfferOrders?.value}
            hasError={summary.pendingOfferOrders?.hasError}
            error={summary.pendingOfferOrders?.error}
            onClick={summary.pendingOfferOrders?.onClick}
            color={summary.pendingOfferOrders?.color}
            alwaysShow
          />
        </Space>
      </Row>
    </Row>
  );
};
